import { faBolt, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import './ViewAIShareWidget.scss';

interface AdminInfo {
    user_id: number;
    user_email: string;
    user_username: string;
    user_country: string;
    user_plan: string;
    website_id: number;
    website_domain: string;
    website_name: string;
    total_user_share_widgets: number;
    total_user_widget_clicks: number;
    widget_clicks_by_llm: Record<string, number>;
}

interface WidgetData {
    id: number;
    widget_id: string;
    name: string;
    prompt_template: string;
    text_before_button: string;
    selected_llms: string[];
    style: string;
    html_code: string;
    html_code_dark: string;
    is_active: boolean;
    is_processing: boolean;
    total_clicks: number;
    created_at: string;
    updated_at: string;
    total_clicks_by_llm: Record<string, number>;
    destination_urls_stats: Record<string, any>;
    admin_info?: AdminInfo;
}

interface AIShareWidgetViewProps {}

const AIShareWidgetView: React.FC<AIShareWidgetViewProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [copiedButton, setCopiedButton] = useState<string | null>(null);
    const [widgetData, setWidgetData] = useState<WidgetData | null>(null);
    const [loading, setLoading] = useState(true);

    const serverUrl = process.env.REACT_APP_DRF_DOMAIN || 'http://localhost:8000';

    useEffect(() => {
        // Get widget data from navigation state
        if (location.state && location.state.widgetData) {
            setWidgetData(location.state.widgetData);
            setLoading(false);
        } else {
            // If no data in state, redirect back or show error
            console.error('No widget data found in navigation state');
            setLoading(false);
        }
    }, [location.state]);

    const copyToClipboard = async (text: string, buttonId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedButton(buttonId);
            
            setTimeout(() => {
                setCopiedButton(null);
            }, 2000);
            
        } catch (err) {
            console.error('Failed to copy text: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            setCopiedButton(buttonId);
            setTimeout(() => {
                setCopiedButton(null);
            }, 2000);
        }
    };

    const getLLMConfig = (llmName: string) => {
        switch(llmName.toLowerCase()) {
            case 'chatgpt':
                return {
                    icon: 'https://staging.draftss2.opalstacked.com/wp-content/uploads/2025/07/chatgpt-2.png',
                    className: 'chatgpt-card',
                    displayName: 'Total ChatGPT Shares'
                };
            case 'google':
            case 'google-ai':
                return {
                    icon: 'https://staging.draftss2.opalstacked.com/wp-content/uploads/2025/07/google.png',
                    className: 'google-card',
                    displayName: 'Total Google AI Shares'
                };
            case 'claude':
                return {
                    icon: 'https://staging.draftss2.opalstacked.com/wp-content/uploads/2025/07/claude-2.png',
                    className: 'claude-card',
                    displayName: 'Total Claude Shares'
                };
            case 'grok':
                return {
                    icon: 'https://staging.draftss2.opalstacked.com/wp-content/uploads/2025/07/grok-2.png',
                    className: 'grok-card',
                    displayName: 'Total Grok Shares'
                };
            case 'perplexity':
                return {
                    icon: 'https://staging.draftss2.opalstacked.com/wp-content/uploads/2025/07/perplexity-2.png',
                    className: 'perplexity-card',
                    displayName: 'Total Perplexity Shares'
                };
            default:
                return {
                    icon: null,
                    className: 'default-card',
                    displayName: `Total ${llmName} Shares`
                };
        }
    };

    if (loading) {
        return (
            <div className="share-page-container">
                <div className="loading-container">
                    <p>Loading widget data...</p>
                </div>
            </div>
        );
    }

    if (!widgetData) {
        return (
            <div className="share-page-container">
                <div className="error-container">
                    <p>Widget data not found</p>
                    <button onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    return(
        <div className="share-page-container">
            {/* Admin Header */}
            <div className="share-admin-card">
                <div className="share-admin-header">
                    <div className="share-admin-header-left">
                        <svg 
                            className="back-btn"
                            width="30"
                            height="24"
                            viewBox="0 0 30 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => navigate(-1)}
                        >
                            <path d="M26.0435 12.0003H2.82031M2.82031 12.0003L12.8382 1.98242M2.82031 12.0003L12.8382 22.0181" stroke="black" strokeOpacity="0.5" strokeWidth="3" />
                        </svg>
                        <p className="share-admin-title">
                            Admin View - AI Share Widget
                        </p>
                    </div>
                </div>
                <div className="share-admin-content">
                    <div className="share-admin-row">
                        <div className="share-admin-col">
                            <strong>Widget ID:</strong> {widgetData.widget_id}
                        </div>
                        <div className="share-admin-col">
                            <strong>Widget Name:</strong> {widgetData.name}
                        </div>
                        <div className="share-admin-col">
                            <strong>Widget Style:</strong> {widgetData.style}
                        </div>
                    </div>
                    {widgetData.admin_info && (
                        <div className="share-admin-row">
                            <div className="share-admin-col">
                                <strong>User:</strong> {widgetData.admin_info.user_email}
                            </div>
                            <div className="share-admin-col">
                                <strong>Website:</strong> {widgetData.admin_info.website_domain}
                            </div>
                            <div className="share-admin-col">
                                <strong>Plan:</strong> {widgetData.admin_info.user_plan}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Analytics Section */}
            <div className="share-content-card">
                <div className="share-content-header">
                    <p className="share-content-title">Analytics</p>
                </div>
                <div className="share-content-body">
                    <div className="ai-share-stats-cards mt-2 mb-5">
                        {/* Total Clicks Card */}
                        <div className="ai-share-stat-card total-clicks">
                            <div className="stat-header">
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={faBolt} />
                                </div>
                                <div className="stat-label">Total Shares</div>
                            </div>
                            <div className="stat-value">{widgetData.total_clicks || 0}</div>
                        </div>

                        {/* All LLM Cards - Show all supported LLMs, even with 0 clicks */}
                        {['chatgpt', 'google', 'claude', 'grok', 'perplexity'].map((llm) => {
                            const count = llm === 'google' 
                                ? (widgetData.total_clicks_by_llm?.['google-ai'] || widgetData.total_clicks_by_llm?.['google'] || 0)
                                : (widgetData.total_clicks_by_llm?.[llm] || 0);
                            
                            const config = getLLMConfig(llm);

                            return (
                                <div key={llm} className={`ai-share-stat-card ${config.className}`}>
                                    <div className="stat-header">
                                        <div className="stat-icon">
                                            {config.icon ? (
                                                <img src={config.icon} alt={config.displayName} />
                                            ) : (
                                                <FontAwesomeIcon icon={faRobot} />
                                            )}
                                        </div>
                                        <div className="stat-label">{config.displayName}</div>
                                    </div>
                                    <div className="stat-value">{count}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>


            <div className="share-content-card">
                <div className="share-content-header">
                    <p className="share-content-title">URL Analytics</p>
                </div>
                <div className="share-content-body">
                    <div className="share-analytics-table">
                        
                        {widgetData.destination_urls_stats && Object.keys(widgetData.destination_urls_stats).length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000000',fontWeight: 'bold'}}>URL</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>Total Clicks</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>ChatGPT</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>Claude</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>Perplexity</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>Grok</th>
                                        <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000000',fontWeight: 'bold'}}>Google AI</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(widgetData.destination_urls_stats).map(([url, stats]) => (
                                        <tr key={url}>
                                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007BFF' }}>
                                                    {url.length > 50 ? `${url.substring(0, 50)}...` : url}
                                                </a>
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.total || 0}
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.by_llm?.chatgpt || 0}
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.by_llm?.claude || 0}
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.by_llm?.perplexity || 0}
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.by_llm?.grok || 0}
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                {stats.by_llm?.['google-ai'] || 0}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p>No analytics data available</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Widget Preview Section */}
            <div className="share-content-card" style={{ marginTop: '20px' }}>
                <div className="share-content-header">
                    <p className="share-content-title">Widget Preview</p>
                </div>
                <div className="share-content-body">
                    <div className="widget-preview-section">
                        <h4>Light Theme</h4>
                        <div 
                            className="widget-iframe-container"
                            style={{
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '20px',
                                backgroundColor: '#ffffff',
                                marginBottom: '20px'
                            }}
                            dangerouslySetInnerHTML={{ __html: widgetData.html_code }}
                        />
                        
                        <h4>Dark Theme</h4>
                        <div 
                            className="widget-iframe-container"  
                            style={{
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '20px',
                                backgroundColor: '#1a1a1a',
                                marginBottom: '20px'
                            }}
                            dangerouslySetInnerHTML={{ __html: widgetData.html_code_dark }}
                        />
                    </div>
                </div>
            </div>

            {/* Embed Code Section */}
            <div className="share-version-card" style={{marginTop: '20px'}}>
                <div className="share-version-header">
                    <p className="share-version-title">
                        Embed Code
                    </p>
                </div>
                <div className="share-version-content">
                    <div className={`share-version-item`}>
                        <div className="share-version-row">
                            <div className="share-version-col">
                                <strong>Script tag: </strong> 
                                <h3>
                                    {`<script src="${serverUrl}/api/frontend/ai-widgets/" defer></script>`}
                                </h3>
                            </div>
                        </div>
                        <div className="share-version-actions">
                            <button 
                                className='share-version-preview-btn'
                                onClick={() => copyToClipboard(`<script src="${serverUrl}/api/frontend/ai-widgets/" defer></script>`, 'script-copy')}
                                style={{
                                    transform: copiedButton === 'script-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'script-copy' ? '#007BFF' : '',
                                    transition: 'all 0.1s ease-in-out',
                                }}
                            >
                                {copiedButton === 'script-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                    
                    <div className={`share-version-item`}>
                        <div className="share-version-row">
                            <div className="share-version-col">
                                <strong>Div tag: </strong>
                                <h3>
                                    {`<div data-widget-id="${widgetData.widget_id}" data-mode="light"></div>`}
                                </h3> 
                            </div>
                        </div>
                        <div className="share-version-actions">
                            <button 
                                className='share-version-preview-btn'
                                onClick={() => copyToClipboard(`<div data-widget-id="${widgetData.widget_id}" data-mode="light"></div>`, 'div-copy')}
                                style={{
                                    transform: copiedButton === 'div-copy' ? 'scale(1.1)' : 'scale(1)',
                                    backgroundColor: copiedButton === 'div-copy' ? '#007BFF' : '',
                                    color: copiedButton === 'div-copy' ? 'white' : '',
                                    transition: 'all 0.2s ease-in-out',
                                    boxShadow: copiedButton === 'div-copy' ? '0 4px 8px rgba(76, 175, 80, 0.3)' : ''
                                }}
                            >
                                {copiedButton === 'div-copy' ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIShareWidgetView;